import { initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import {
  getAuth, GoogleAuthProvider, setPersistence, browserLocalPersistence,
  connectAuthEmulator, type Auth,
} from 'firebase/auth';
import {
  getFirestore, connectFirestoreEmulator, initializeFirestore, type Firestore,
} from 'firebase/firestore';

let firebaseConfig: FirebaseOptions | null = null;
let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.0.0.1');
export const isIdx = window.location.hostname.includes('idx.dev') || window.location.hostname.includes('cloudworkstations.dev');
const isDevelopment = import.meta.env.DEV || isLocalhost || isIdx;

type EmulatorConfig = { host: string; protocol: string; url: string };

function getEmulatorUrl(targetPort: number): EmulatorConfig {
  if (isIdx) {
    const currentHost = window.location.hostname;
    const newHost = currentHost.replace(/^\d+/, String(targetPort));
    return { host: newHost, protocol: 'https', url: `https://${newHost}` };
  }
  const host = window.location.hostname;
  return { host, protocol: 'http', url: `http://${host}:${targetPort}` };
}

export async function initializeFirebase(): Promise<{ app: FirebaseApp; auth: Auth; db: Firestore }> {
  try {
    const envFirebaseConfig: FirebaseOptions = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    };

    if (isDevelopment) {
      firebaseConfig = envFirebaseConfig;
      if (!firebaseConfig.projectId) {
        throw new Error('Missing VITE_FIREBASE_* config for development environment.');
      }
      console.log('✅ Loaded Firebase config from VITE_FIREBASE_* env values');
    } else {
      try {
        const response = await fetch('/__/firebase/init.json');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        firebaseConfig = await response.json() as FirebaseOptions;
        console.log('✅ Loaded Firebase config from /__/firebase/init.json');
      } catch {
        console.warn('⚠️ Could not load /__/firebase/init.json, using env fallback config.');
        firebaseConfig = envFirebaseConfig;
      }
      if (!firebaseConfig.projectId) {
        throw new Error('Missing Firebase config. Provide Hosting /__/firebase/init.json or VITE_FIREBASE_* values.');
      }
    }

    app = initializeApp(firebaseConfig);
    auth = getAuth(app);

    if (isDevelopment) {
      console.log(`🚧 Running in ${isIdx ? 'Project IDX' : 'Localhost'} Development Mode`);

      try {
        const authConfig = getEmulatorUrl(9099);
        connectAuthEmulator(auth, authConfig.url, { disableWarnings: true });
        console.log(`🔌 Connected to Auth Emulator at: ${authConfig.url}`);
      } catch (authError) {
        const msg = authError instanceof Error ? authError.message : String(authError);
        if (!msg.includes('already been called')) {
          console.warn('Auth emulator connection warning:', msg);
        }
      }

      if (isIdx) {
        const firestoreConfig = getEmulatorUrl(8080);
        db = initializeFirestore(app, { host: firestoreConfig.host, ssl: true });
        console.log(`🔌 Initialized Firestore for IDX Emulator at: https://${firestoreConfig.host}`);
      } else {
        db = getFirestore(app);
        const firestoreConfig = getEmulatorUrl(8080);
        connectFirestoreEmulator(db, firestoreConfig.host, 8080);
        console.log(`🔌 Connected to Firestore Emulator at: http://${firestoreConfig.host}:8080`);
      }
    } else {
      db = getFirestore(app);
    }

    console.log('🚀 Connected to Firebase Project:', firebaseConfig.projectId, isDevelopment ? '(Development)' : '(Production)');

    return { app, auth, db };
  } catch (error) {
    console.error('Firebase Initialization Error:', error);
    throw error;
  }
}

export async function getAuthInstance(): Promise<Auth> {
  if (!auth) await initializeFirebase();
  return auth!;
}

export async function getFirestoreInstance(): Promise<Firestore> {
  if (!db) await initializeFirebase();
  return db!;
}

export function getGoogleAuthProvider(): GoogleAuthProvider {
  return new GoogleAuthProvider();
}

export async function setAuthPersistence(): Promise<void> {
  const authInstance = await getAuthInstance();
  await setPersistence(authInstance, browserLocalPersistence);
}
